<template>
    <div class="m-0 p-0">

        <div class="container-fluid px-4 my-2">

            <vue-good-wizard
                    ref="wizard"
                    finalStepLabel="Done"
                    :steps="steps"
                    :onNext="nextClicked"
                    :onBack="backClicked">
                <div slot="page1">
                    <h4> <img width="42px" height="42px" src="../../assets/navlogo.png" class="mr-3 ml-0 p-0" >Getting Started</h4>
                    <div class="container-fluid mt-4">
                        <div class="row">
                            <div class="col-12">

                                <p>VO2 Master Cloud is a data synchronization, analysis, and reporting platform that collects data from a plethora of physiologic sensors. It collects data using the VO2 Master Manager phone application <a href="https://bit.ly/2EpRwAx">(www.vo2master.com/mobile-application/)</a>.</p>

                                <p>No matter where your athletes are, you can view their sessions in real-time through VO2 Master Cloud so long as their phone has an internet connection. Remote coaching with real-time, individualized assessment is now possible.</p>

                                <p>With this system you can measure your:</p>
                                <ul>
                                <li>Metabolism 		(VO2)</li>
                                <li>Respiration 		(ventilation, tidal volume, breathing frequency)</li>
                                <li>Cardiovascular 	(heart rate, heart rate variability)</li>
                                <li>Muscle Oxygenation 	(SMO2, THB, triple sensor measurement)</li>
                                <li>Mechanics 		(cadence, speed, power, dual-sided power)</li>
                                </ul>

                                <p>Best of all it’s completely free – even if you don’t own a VO2 Master Pro.</p>
                                <p>To learn more about VO2 Master, visit <a href="https://bit.ly/2D3o3Mc">www.vo2master.com</a></p>

                            </div>
                        </div>
                    </div>
                </div>
                <div slot="page2">
                    <h4><font-awesome-icon icon="user" class="mr-3 ml-0 p-0" />Account Details</h4>
                    <p>Please enter the following details to complete your account setup for <b>{{userAccountName}}</b> </p>
                    <p>This information is only visible to you. Your Athletes can not see it.</p>

                    <b-overlay :show="isUserAccountLoading" rounded="sm">


                        <ErrorView :visible="isUserAccountLoadingError" :errorValue="userAccountLoadingErrorValue"/>

                    <div class="container-fluid mt-4">
                        <form ref="form" v-on:submit.prevent="noop">
                            <b-form-group
                                    label="First Name"
                                    label-for="firstname-input"
                                    invalid-feedback="First Name is required (minimum 3 characters, max 200)"
                                    :state="firstNameState">
                                <b-form-input
                                        id="firstname-input"
                                        placeholder="Enter first name"
                                        v-model="firstNameValue"
                                        required>
                                </b-form-input>
                            </b-form-group>
                            <b-form-group
                                    label="Last Name"
                                    label-for="lastname-input"
                                    invalid-feedback="Last Name is required (minimum 3 characters, max 200)"
                                    :state="lastNameState">
                                <b-form-input
                                        id="lastname-input"
                                        placeholder="Enter last name"
                                        v-model="lastNameValue"
                                        required>
                                </b-form-input>
                            </b-form-group>

                          <b-form-group
                              label="Country"
                              label-for="country-input"
                          >
                            <country-select id="country-input"
                                            className="form-control"
                                            v-model="countryValue"
                                            :country="countryValue"
                                            topCountry="US" />
                          </b-form-group>

                            <b-form-group
                                    label="Company Name"
                                    label-for="companyname-input"
                                    invalid-feedback="(max 200 characters)"
                                    :state="companyNameState">
                                <b-form-input
                                        id="companyname-input"
                                        placeholder=""
                                        v-model="companyNameValue"
                                >
                                </b-form-input>
                            </b-form-group>
                            <b-form-group
                                    label="Phone Number"
                                    label-for="phonenumber-input"
                                    invalid-feedback="(max 20 characters)"
                                    :state="phoneNumberState">
                                <b-form-input
                                        id="phonenumber-input"
                                        placeholder="... ... ...."
                                        v-model="phoneNumberValue"
                                >
                                </b-form-input>
                            </b-form-group>
                        </form>
                    </div>
                    </b-overlay>
                </div>
                <div slot="page3">
                    <h4><font-awesome-icon icon="users" class="mr-3 ml-0 p-0"/>Create Organization</h4>
                    <p>Organizations can be used to group athlete profiles and sessions. You can create multiple organizations and invite other users to enable data sharing.</p>
                    <b-overlay :show="isOrganizationLoading" rounded="sm">

                        <ErrorView :visible="isOrganizationLoadingError" :errorValue="organizationLoadingErrorValue"/>

                    <div class="container-fluid mt-4">
                        <form ref="form" v-on:submit.prevent="noop">
                            <b-form-group
                                    label="Name (e.g company name, team name or group)"
                                    label-for="name-input"
                                    invalid-feedback="Name is required (minimum 3 characters, max 200)"
                                    :state="orgNameState">
                                <b-form-input
                                        id="name-input"
                                        placeholder="Enter name for Organization"
                                        v-model="orgNameValue"
                                        required>
                                </b-form-input>
                            </b-form-group>
                        </form>
                    </div>
                    </b-overlay>
                </div>
                <div slot="page4">
                    <h4> <img width="42px" height="42px" src="../../assets/navlogo.png" class="mr-3 ml-0 p-0" >Download</h4>
                    <div class="container-fluid mt-4">
                        <div class="row">
                            <div class="col-12">

                                <p>Download the VO2 Master Manager phone application if you haven’t already. This allows you to collect data remotely, and view it live on your computer. Follow the in-app instructions to complete the setup process.</p>

                                <p>
                                    <a href='https://bit.ly/3holWkV'><img height="56px" alt='Get it on iOS App Store' src='../../assets/iosapp.png'/></a>
                                    <a href='https://bit.ly/3hGTStn'><img height="80px" alt='Get it on Google Play' src='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'/></a>
                                </p>

                                <br/>

                                <h5>Apple (iOS): <a href="https://bit.ly/3holWkV"><small>https://getapp.cc/app/936519055</small></a> </h5>
                                <h5>Android: <a href="https://bit.ly/3hGTStn"><small>https://play.google.com/store/apps/details?id=com.vo2master.vo2master</small></a></h5>

                                <br/>
                                <br/>

                                <p>
                                    <b>For more information: </b>
                                    <a href="https://bit.ly/2EpRwAx">https://vo2master.com/mobile-application/</a>
                                </p>

                            </div>
                        </div>
                    </div>
                </div>
            </vue-good-wizard>

        </div>

        <b-modal id="error-modal" size="sm" title="Action failed" ok-only centered>
            <p class="m-2">There was a problem completing the action, Please try again.</p>
        </b-modal>

    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { GoodWizard } from 'vue-good-wizard';
    import ErrorView from '../ErrorView'

    export default {
        name: "UserSetupPage",
        components: {
            'vue-good-wizard': GoodWizard,
            ErrorView
        },
        data() {
            return {



                steps: [
                    {
                        label: '1. Introduction',
                        slot: 'page1',
                    },
                    {
                        label: '2. Account Details',
                        slot: 'page2',
                    },
                    {
                        label: '3. Organization Details',
                        slot: 'page3',
                    },
                    {
                        label: '4. Download Phone App',
                        slot: 'page4',
                    },
                ],


                //user account
                firstNameValue: null,
                lastNameValue: null,

                companyNameValue: null,
                phoneNumberValue: null,

                countryValue: null,

                //org
                orgNameValue: null,


                showValidation: false,
            }
        },
        created () {

            this.resetUserAccount(this.userAccount);
            this.resetOrganization(this.organization);

            this.refreshOrganization();

        },
        computed: {
            ...mapGetters({

                userAccount: 'userAccount/current',
                userAccountName: 'userAccount/currentName',
                isUserAccountLoading: 'userAccount/currentLoading',
                isUserAccountLoadingError: 'userAccount/currentLoadingError',
                userAccountLoadingErrorValue: 'userAccount/currentLoadingErrorValue',

                organization: 'organization/current',
                organizationName: 'organization/currentName',
                isOrganizationLoading: 'organization/currentLoading',
                isOrganizationLoadingError: 'organization/currentLoadingError',
                organizationLoadingErrorValue: 'organization/currentLoadingErrorValue',
            }),

            firstNameState: function() {
                if(this.firstNameValue && this.firstNameValue.length > 2 && this.firstNameValue.length < 200) {
                    return true
                }
                return this.showValidation ? false : null
            },
            lastNameState: function() {
                if(this.lastNameValue && this.lastNameValue.length > 2 && this.lastNameValue.length < 200) {
                    return true
                }
                return this.showValidation ? false : null
            },

            companyNameState: function() {
                if(this.companyNameValue) {
                    if(this.companyNameValue.length  < 200)
                        return true;
                    else
                        return this.showValidation ? false : null
                }
                return true
            },
            phoneNumberState: function() {
                if(this.phoneNumberValue) {
                    if(this.phoneNumberValue.length  < 20)
                        return true;
                    else
                        return this.showValidation ? false : null
                }
                return true
            },

            validatedUserAccountValue: function() {

                if(this.isUserAccountLoading ||this.isUserAccountLoadingError) {
                    return null;
                }

                if(this.firstNameState &&
                    this.lastNameState &&
                    this.phoneNumberState &&
                    this.companyNameState) {
                    let value = {
                        userAccountId: this.userAccount ? this.userAccount.userAccountId : undefined,
                        firstName: this.firstNameValue,
                        lastName: this.lastNameValue,
                        companyName: this.companyNameValue,
                        phoneNumber: this.phoneNumberValue,
                        countryCode: this.countryValue,
                    };

                    return value;
                }

                return null;
            },


            orgNameState: function () {
                if (this.orgNameValue && this.orgNameValue.length > 2 && this.orgNameValue.length < 200) {
                    return true
                }
                return this.showValidation ? false : null
            },
            validatedOrganizationValue: function() {

                if(this.orgNameState ) {
                    let value = {
                        organizationId: this.organization ? this.organization.organizationId : undefined,
                        name: this.orgNameValue,
                    };

                    return value;
                }

                return null;
            },
        },
        watch: {
            userAccount(newValue) {
                this.resetUserAccount(newValue);
            },
            organization(newValue) {
                this.resetOrganization(newValue);
            },
        },
        methods: {
            ...mapActions({
                saveUserAccount: 'userAccount/save',

                saveOrganization: 'organization/save',
                selectOrganization: 'organization/select',
            }),
            refreshOrganization() {
                return this.selectOrganization(this.organizationId)
            },
            resetUserAccount(newValue) {
                this.showValidation = false;
                if(newValue) {
                    this.firstNameValue = newValue.firstName;
                    this.lastNameValue = newValue.lastName;
                    this.companyNameValue = newValue.companyName;
                    this.phoneNumberValue = newValue.phoneNumber;
                    this.countryValue = newValue.countryCode;
                } else {
                    this.firstNameValue = null;
                    this.lastNameValue = null;
                    this.companyNameValue = null;
                    this.phoneNumberValue = null;
                    this.countryValue = null;
                }
            },
            resetOrganization(newValue) {
                this.showValidation = false;
                if (newValue) {
                    this.orgNameValue = newValue.name;
                } else {
                    this.orgNameValue = null;
                }
            },

            nextClicked(currentPage) {

                let self = this;
                switch(currentPage) {
                    case 0:
                    {
                        this.showValidation = false;
                    }
                        break;
                    case 1:
                    {
                        this.handleUserAccountSetup().then(function (success) {
                            if(success && self.userAccount) {
                                //success
                                let orgId = self.userAccount.userSettings.defaultOrganizationId;
                                if (orgId && orgId != '') {
                                    self.organizationId = orgId;
                                    self.refreshOrganization();
                                    self.$refs.wizard.goTo(3);
                                } else {
                                    self.$refs.wizard.goNext(true);
                                }
                                return true;

                            } else {

                                if(!self.showValidation)
                                    self.$bvModal.show('error-modal')

                                //error
                                return false;
                            }
                        });

                        return false;
                    }
                    case 2:
                    {
                        this.handleOrganizationSetup().then(function (success) {
                            if(success && self.organization) {
                                self.$refs.wizard.goNext(true);
                                return true;
                            } else {

                                if(!self.showValidation)
                                    self.$bvModal.show('error-modal')
                                //error
                                return false;
                            }
                        });

                       return false;
                    }
                    case 3:
                    {
                        if(self.organization) {
                            let orgId = self.organization.organizationId;
                            if (orgId !== undefined && orgId != '') {
                                self.$router.push({name: 'Dashboard', params: {orgId: orgId}});
                                return true;
                            }
                        }

                        self.$router.push({name: 'OrganizationList' });
                        return false;
                    }
                }

                return true; //return false if you want to prevent moving to next page
            },
            backClicked(currentPage) {
                this.showValidation = false;

                let self = this;
                switch(currentPage) {
                    case 3: {
                        let orgId = self.organizationId;
                        if (orgId !== undefined && orgId != '') {
                            self.$refs.wizard.goTo(1);
                            return false;
                        }
                    }
                    break;
                }

                return true; //return false if you want to prevent moving to previous page
            },


            handleUserAccountSetup() {

                if(this.isUserAccountLoading) {
                    return new Promise(resolve => resolve(false));
                }

                if(this.validatedUserAccountValue != null) {
                    this.showValidation = false;
                    return this.saveUserAccount(this.validatedUserAccountValue);
                } else {
                    this.showValidation = true;
                    return new Promise(resolve => resolve(false));
                }
            },

            handleOrganizationSetup() {

                if (this.isOrganizationLoading) {
                    return new Promise(resolve => resolve(false));
                }

                if (this.validatedOrganizationValue != null) {
                    this.showValidation = false;
                    return this.saveOrganization(this.validatedOrganizationValue);
                } else {
                    this.showValidation = true;
                    return new Promise(resolve => resolve(false));
                }
            },

        }
    }
</script>

<style scoped>

</style>